/**
 * Chat commands (`/help`, `/gamemode`, ...). Pure logic: the caller owns the
 * overlay and passes a context of game callbacks, so this file never touches
 * the DOM, the player or the world itself (and stays unit-testable in the node
 * smoke run).
 *
 *   runChatCommand(ctx, '/give diamond_sword 1');
 *
 * Every failure path prints a chat line; nothing here throws at the caller.
 * Any player may run commands — each one acts on the player who typed it (its
 * own body, inventory, mode); the world-wide ones (/time, /summon) are replayed
 * by the host, which owns the clock and the mobs. The ones that change the game
 * are also behind a pin — `/pin <pin>`, see below — and a pin line is
 * local-only (isSecretCommand keeps it out of the room).
 */

import { BLOCKS } from '../blocks';
import { ALL_ITEM_IDS, itemNameOf } from '../game/itemIds';
import { itemDef } from '../game/itemdb';
import { weatherCommand } from '../game/weather';
import type { GameMode, MobType } from '../game/types';

/** Everything a command can touch. `main.ts` builds one of these. */
export interface CommandContext {
  /** Print a system line in the chat overlay. */
  say(text: string): void;
  gameMode(): GameMode;
  setGameMode(m: GameMode): void;
  /** Sky time as a 0..1 fraction of the day (0 = 06:00 dawn, 0.25 = noon). */
  time(): number;
  setTime(t: number): void;
  position(): { x: number; y: number; z: number };
  teleport(x: number, y: number, z: number): void;
  /** Nearby structures for `/tp <structure>`; main.ts runs the same scan as the teleport picker. */
  structures?(): readonly StructureSpot[];
  /** The other players of a session for `/tp <player>`; empty in single player. */
  players?(): readonly PlayerSpot[];
  /** Add items; whatever does not fit is dropped at the player. */
  give(id: number, count: number): void;
  /** Spawn in front of the player. */
  spawnMob(type: MobType, count: number): void;
  /** Kill the player (works in creative too, like vanilla /kill). */
  kill(): boolean;
  clearInventory(): void;
  seed(): number;
}

/** A structure near the player, already resolved to a safe standing spot. */
export interface StructureSpot { name: string; dist: number; x: number; y: number; z: number }

/** Another player in the session, with their latest position. */
export interface PlayerSpot { id: string; name: string; slot: number; x: number; y: number; z: number }

// --- parsing -----------------------------------------------------------------

export interface ParsedCommand { name: string; args: string[] }

/** `/name arg arg` -> { name, args }. Null when the line is not a command. */
export function parseCommand(line: string): ParsedCommand | null {
  const trimmed = line.trim();
  if (!trimmed.startsWith('/') || trimmed.length < 2) return null;
  const body = trimmed.slice(1);
  const sp = body.search(/\s/);
  const name = (sp < 0 ? body : body.slice(0, sp)).toLowerCase();
  if (!/^[a-z_]+$/.test(name)) return null;
  const args = (sp < 0 ? '' : body.slice(sp + 1)).trim().split(/\s+/).filter(Boolean);
  return { name, args };
}

/**
 * How well a name answers a query: exact (4), first word (3), prefix (2) or
 * anywhere inside (1). `/tp village` lands on "Village", `/tp outpost` on
 * "Pillage Outpost" — the word tier is what makes that one work.
 */
function nameScore(rawName: string, q: string): number {
  const name = norm(rawName);
  if (!q) return 0;
  if (name === q) return 4;
  const words = name.split(' ');
  if (words[0] === q) return 3;
  if (words.some((w) => w.startsWith(q))) return 3;
  if (name.startsWith(q)) return 2;
  return name.includes(q) ? 1 : 0;
}

/**
 * Best match for `q` in `rows`, or null. `extra` lets a row be found by
 * something other than its name (a player's id or slot). Ties go to the
 * nearest row, then to the first one.
 */
function bestMatch<T extends { name: string; dist?: number }>(
  q: string, rows: readonly T[], extra?: (row: T, q: string) => boolean,
): T | null {
  let best: T | null = null;
  let bestScore = 0;
  let bestDist = Infinity;
  for (const row of rows) {
    const score = extra?.(row, q) ? 5 : nameScore(row.name, q);
    if (!score) continue;
    const dist = row.dist ?? 0;
    if (score > bestScore || (score === bestScore && dist < bestDist)) {
      best = row; bestScore = score; bestDist = dist;
    }
  }
  return best;
}

// --- name lookup -------------------------------------------------------------

/** "Diamond Sword" / "diamond_sword" / "diamondSword" all normalize alike. */
const norm = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

let itemIndex: Map<string, number> | null = null;

/** Display name (and registry name) -> item or block id. Blocks win ties. */
export function itemByName(name: string): number | undefined {
  if (!itemIndex) {
    const m = new Map<string, number>();
    const put = (key: string | undefined, id: number): void => {
      const k = norm(key ?? '');
      if (k && !m.has(k)) m.set(k, id);
    };
    BLOCKS.forEach((b, id) => put(b?.name, id));
    for (const id of ALL_ITEM_IDS) {
      put(itemNameOf(id), id);
      put(itemDef(id).name, id);
    }
    itemIndex = m;
  }
  return itemIndex.get(norm(name));
}

/** Every mob the world can spawn (entities/manager.spawnMob takes these). */
const MOB_TYPES: readonly MobType[] = [
  'zombie', 'skeleton', 'creeper', 'spider', 'pig', 'cow', 'sheep', 'chicken',
  'zombified_piglin', 'piglin', 'magma_cube', 'ghast', 'blaze', 'wither_skeleton',
  'hoglin', 'strider', 'enderman',
  'villager', 'iron_golem', 'pillager', 'vindicator', 'zombie_villager',
  'slime', 'cave_spider', 'husk', 'stray', 'drowned', 'silverfish', 'endermite', 'wolf', 'witch', 'phantom', 'cat', 'squid', 'cod', 'salmon', 'bat',
  'panda', 'parrot', 'fox', 'wither', 'warden',
  'wandering_trader', 'llama', 'trader_llama',
];

export function mobByName(name: string): MobType | undefined {
  return MOB_TYPES.find((t) => norm(t) === norm(name));
}

// --- argument helpers --------------------------------------------------------

/** Strict non-negative integer, or undefined ("12.5" and "1e3" are rejected). */
function countArg(raw: string | undefined, max: number): number | 'bad' | undefined {
  if (raw === undefined) return undefined;
  if (!/^\d+$/.test(raw)) return 'bad';
  const n = parseInt(raw, 10);
  if (n < 1) return 'bad';
  return Math.min(n, max);
}

/** A coordinate: a plain number, or `~` / `~12` relative to the player. */
function coordArg(raw: string, base: number): number | null {
  if (raw.startsWith('~')) {
    const rest = raw.slice(1);
    if (rest === '') return base;
    if (!/^-?\d+(\.\d+)?$/.test(rest)) return null;
    return base + parseFloat(rest);
  }
  if (!/^-?\d+(\.\d+)?$/.test(raw)) return null;
  return parseFloat(raw);
}

/** A day time in vanilla ticks (0..24000) or undefined. */
const TIME_WORDS: Record<string, number> = { day: 1000, night: 13000, noon: 6000, midnight: 18000 };
function tickArg(raw: string): number | null {
  const word = TIME_WORDS[raw.toLowerCase()];
  if (word !== undefined) return word;
  if (!/^\d+$/.test(raw)) return null;
  return parseInt(raw, 10);
}

const TIME_LABEL = (t: number): string => `${Math.round(((t * 24000) % 24000 + 24000) % 24000)}`;

// --- commands ----------------------------------------------------------------

export const COMMAND_HELP: readonly string[] = [
  '/help - this list',
  '/gamemode creative|survival - switch mode',
  '/time set day|night|noon|midnight|<ticks> - set the time',
  '/time add <ticks> - move the clock on',
  '/weather clear|rain|thunder [seconds] - set the weather',
  '/tp <x> <y> <z> - teleport (~ is relative)',
  '/tp <structure> - nearest structure by name (/tp village, /tp outpost)',
  '/tp <player> - another player in the session (name or id)',
  '/give <item> [count] - give items',
  '/summon <mob> [count] - spawn mobs in front of you',
  '/kill - die',
  '/clear - empty your inventory',
  '/seed - show the world seed',
];

// --- the pin gate ------------------------------------------------------------

/**
 * The commands that change the game (LOCKED, below) stay shut until the pin is
 * typed once, as `/pin <pin>`; /help and /seed are free. Only the pin's hash is
 * written down, so reading the source or the bundle does not hand it over. Two
 * FNV-1a passes: obfuscation, not cryptography (32 bits of state each, and a
 * short pin is brute-forceable by anyone who can call this).
 *
 * To change the pin, compute pinHash(the new pin) and paste it into PIN_HASH.
 */
export function pinHash(s: string): string {
  let a = 0x811c9dc5, b = 0x01000193;
  for (let i = 0; i < s.length; i++) {
    const c = s.charCodeAt(i);
    a = Math.imul(a ^ c, 0x01000193) >>> 0;
    b = Math.imul(b ^ (c + 31), 0x85ebca6b) >>> 0;
  }
  return a.toString(16).padStart(8, '0') + b.toString(16).padStart(8, '0');
}

/** pinHash(current pin). */
const PIN_HASH = '22089d5d36518523';

/** Commands that need the pin: they move the player, the clock or the world. */
const LOCKED = new Set(['gamemode', 'time', 'weather', 'give', 'summon', 'kill', 'clear']);

/** Entering the pin unlocks the locked commands for good (remembered in localStorage). */
const UNLOCK_KEY = 'opencraft.cmdUnlocked';
function loadUnlocked(): boolean {
  try { return globalThis.localStorage?.getItem(UNLOCK_KEY) === '1'; } catch { return false; }
}
function saveUnlocked(v: boolean): void {
  try { if (v) globalThis.localStorage?.setItem(UNLOCK_KEY, '1'); else globalThis.localStorage?.removeItem(UNLOCK_KEY); } catch { /* no storage */ }
}
let unlocked = loadUnlocked();

/** Unlock/lock without the pin: node smoke tests and dev tools only. */
export function unlockCommands(): void { unlocked = true; }
export function lockCommands(): void { unlocked = false; saveUnlocked(false); }

/** Whether the pin has been accepted yet. */
export function commandsUnlocked(): boolean { return unlocked; }

/**
 * Lines that must not leave this client: relayed or echoed, the pin would end
 * up in every peer's chat log. main.ts checks this before session.chat().
 */
export function isSecretCommand(line: string): boolean {
  return parseCommand(line)?.name === 'pin';
}

type Handler = (ctx: CommandContext, args: string[]) => void;

const COMMANDS: Record<string, Handler> = {
  help: (ctx, args) => {
    if (args.length > 0) { ctx.say(`Usage: ${COMMAND_HELP[0]}`); return; }
    for (const line of COMMAND_HELP) ctx.say(line);
  },

  gamemode: (ctx, args) => {
    if (args.length === 0) { ctx.say(`Game mode: ${ctx.gameMode()}`); return; }
    const raw = args[0].toLowerCase();
    const m: GameMode | undefined =
      raw === 'creative' || raw === 'c' || raw === '1' ? 'creative'
      : raw === 'survival' || raw === 's' || raw === '0' ? 'survival'
      : undefined;
    if (!m) { ctx.say('Usage: /gamemode creative|survival'); return; }
    ctx.setGameMode(m);
    ctx.say(`Game mode set to ${m}`);
  },

  time: (ctx, args) => {
    const rest = args[0]?.toLowerCase() === 'set' || args[0]?.toLowerCase() === 'add'
      ? args.slice(1) : args;
    const verb = args[0]?.toLowerCase() === 'add' ? 'add' : 'set';
    if (rest.length === 0) { ctx.say(`Time: ${TIME_LABEL(ctx.time())}`); return; }
    const ticks = tickArg(rest[0]);
    if (ticks === null) { ctx.say('Usage: /time set day|night|noon|midnight|<ticks>'); return; }
    const day = ticks % 24000;
    ctx.setTime(verb === 'add' ? (ctx.time() + day / 24000) % 1 : day / 24000);
    ctx.say(verb === 'add' ? `Added ${day} to the time` : `Set the time to ${day}`);
  },

  weather: (ctx, args) => { ctx.say(weatherCommand(args)); },

  /** `/tp <x> <y> <z>` (coordinates), `/tp <structure>` or `/tp <player>`. */
  tp: (ctx, args) => {
    if (args.length === 3) {
      const p = ctx.position();
      const x = coordArg(args[0], p.x), y = coordArg(args[1], p.y), z = coordArg(args[2], p.z);
      if (x === null || y === null || z === null) { ctx.say('Usage: /tp <x> <y> <z> (numbers, or ~ for relative)'); return; }
      ctx.teleport(x, y, z);
      ctx.say(`Teleported to ${x.toFixed(1)} ${y.toFixed(1)} ${z.toFixed(1)}`);
      return;
    }
    if (args.length !== 1) {
      ctx.say('Usage: /tp <x> <y> <z> | /tp <structure> | /tp <player>');
      return;
    }
    const q = norm(args[0]);
    const players = ctx.players?.() ?? [];
    const structures = ctx.structures?.() ?? [];
    const here = ctx.position();
    const distTo = (p: { x: number; y: number; z: number }): number =>
      Math.hypot(p.x - here.x, p.y - here.y, p.z - here.z);
    // a player wins over a structure: names are unique, structure kinds repeat
    const who = bestMatch(q, players, (p, query) => p.id === query || (p.slot >= 0 && String(p.slot) === query));
    if (who) {
      ctx.teleport(who.x, who.y, who.z);
      ctx.say(`Teleported to ${who.name} (${Math.round(distTo(who))}m)`);
      return;
    }
    const place = bestMatch(q, structures);
    if (place) {
      ctx.teleport(place.x, place.y, place.z);
      ctx.say(`Teleported to ${place.name} (${Math.round(place.dist)}m)`);
      return;
    }
    // nothing matched: say what IS here, so the next try can be a real name
    const parts: string[] = [players.length
      ? `players: ${players.map((p) => p.name).join(', ')}`
      : 'no other players'];
    if (structures.length) parts.push(`nearby: ${structures.map((s) => `${s.name} ${Math.round(s.dist)}m`).join(', ')}`);
    ctx.say(`Nothing matches "${args[0]}" (${parts.join('; ')})`);
  },

  give: (ctx, args) => {
    if (args.length === 0) { ctx.say('Usage: /give <item> [count]'); return; }
    const id = itemByName(args[0]);
    if (id === undefined || id <= 0) { ctx.say(`Unknown item: ${args[0]}`); return; }
    const n = countArg(args[1], 2304);
    if (n === 'bad') { ctx.say('Usage: /give <item> [count] (count must be a whole number)'); return; }
    const count = n ?? 1;
    ctx.give(id, count);
    ctx.say(`Gave ${count} × ${itemDef(id).name || args[0]}`);
  },

  summon: (ctx, args) => {
    if (args.length === 0) { ctx.say('Usage: /summon <mob> [count]'); return; }
    const type = mobByName(args[0]);
    if (!type) { ctx.say(`Unknown mob: ${args[0]}`); return; }
    const n = countArg(args[1], 32);
    if (n === 'bad') { ctx.say('Usage: /summon <mob> [count] (count must be a whole number)'); return; }
    const count = n ?? 1;
    ctx.spawnMob(type, count);
    ctx.say(count === 1 ? `Summoned a ${type.replace(/_/g, ' ')}` : `Summoned ${count} ${type.replace(/_/g, ' ')}s`);
  },

  kill: (ctx) => {
    if (!ctx.kill()) ctx.say('Nothing to kill');
  },

  clear: (ctx) => {
    ctx.clearInventory();
    ctx.say('Cleared your inventory');
  },

  seed: (ctx) => { ctx.say(`Seed: ${ctx.seed()}`); },

  /** Never relayed or echoed (see isSecretCommand): the line is local only. */
  pin: (ctx, args) => {
    const pin = args.join(' ');
    if (!pin) { ctx.say('Usage: /pin <pin>'); return; }
    if (pinHash(pin) !== PIN_HASH) { ctx.say('Wrong pin'); return; }
    unlocked = true;
    saveUnlocked(true);
    ctx.say(`Unlocked: ${[...LOCKED].map((c) => '/' + c).join(' ')}`);
  },
};

/**
 * Run one chat line if it is a command. Non-commands and refusals are silent
 * (the caller already echoed the message); everything else answers in chat.
 */
export function runChatCommand(ctx: CommandContext, line: string): void {
  const parsed = parseCommand(line);
  if (!parsed) return;
  try {
    const cmd = COMMANDS[parsed.name];
    if (!cmd) { ctx.say(`Unknown command: /${parsed.name} - try /help`); return; }
    if (!unlocked && LOCKED.has(parsed.name)) { ctx.say(`/${parsed.name} is locked - /pin <pin> unlocks it`); return; }
    cmd(ctx, parsed.args);
  } catch (e) {
    ctx.say(`Command failed: ${String((e as Error)?.message ?? e).slice(0, 120)}`);
  }
}
